<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import LiteYouTubeEmbed from 'vue-lite-youtube-embed';
import { vueVimeoPlayer } from 'vue-vimeo-player';

import { useCurrentLocale } from '@/plugins/i18n';
import { LanguageTranslation, Locales } from '@/store/language.interface';
import { getTranslation } from '@/utils/data-display-utils';

// PROPS
const props = defineProps<{
  type: string;
  title?: LanguageTranslation;
  videoId: LanguageTranslation;
  languageEnabled?: Locales[];
}>();

// TYPES
type ImageResolution = 'default' | 'maxresdefault' | 'sddefault' | 'hqdefault' | 'mqdefault';

// REFS
const poster = ref<ImageResolution>('default');
const locale = useCurrentLocale();

// LIFECYCLE
onMounted(async () => {
  if (props.type === 'Youtube') {
    poster.value = await getPoster();
  }
});

// FUNCTIONS
async function getPoster(): Promise<ImageResolution> {
  if (!props.languageEnabled) return 'default';
  const img = new Image();
  const videoId = getTranslation(props, 'videoId', props.languageEnabled);
  const resolutions = ['maxresdefault', 'sddefault', 'hqdefault'] as ImageResolution[];
  for (const resolution of resolutions) {
    const url = `https://i.ytimg.com/vi/${videoId}/${resolution}.jpg`;
    const loaded = await new Promise<boolean>((resolve) => {
      img.onload = () => resolve(img.naturalWidth > 120);
      img.onerror = () => resolve(false);
      img.src = url;
    });
    if (loaded) return resolution;
  }
  return 'default';
}

// WATCH
watch(locale, async () => {
  if (props.type === 'Youtube') {
    poster.value = await getPoster();
  }
});
</script>
<template>
  <div class="video-player">
    <div v-if="props.type === 'Youtube'">
      <LiteYouTubeEmbed
        :id="getTranslation(props, 'videoId', props.languageEnabled)"
        :title="getTranslation(props, 'title', props.languageEnabled)"
        :poster="poster"
      />
    </div>
    <div v-else>
      <vue-vimeo-player ref="player" :video-id="getTranslation(props, 'videoId', props.languageEnabled)" />
    </div>
  </div>
</template>
<style lang="scss">
.video-player {
  iframe {
    width: 100% !important;
  }
}
</style>
